// @ts-check
// Prints the price the tap earns, instead of leaving the old one on screen for the length of the
// page-sized round trip variant-picker.js starts. Which price that is comes from the table:
// snippets/custom.price-preview.liquid names the axes that pin the quote, and the cheapest row
// behind the rest is what a page hiding an axis quotes. Repaints the CTA pill's badge alongside the
// price block, because the two sit on screen together and a badge left behind reads as the bug.

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * One row of the table snippets/custom.price-preview.liquid emits.
 *
 * @typedef {object} QuotedVariant
 * @property {string[]} values - Option values in option order, as `variant.options` gives them.
 * @property {number} price - Price in cents.
 * @property {number | null} compareAtPrice - Compare-at price in cents, or null.
 */

/**
 * Repaints the product page's prices from a table already in the page.
 *
 * The server render is still the authority: it arrives moments later and replaces this markup. This
 * only removes the wait, and it stands down wherever it cannot reproduce the server's own output.
 *
 * @extends Component<{}>
 */
class PricePreview extends Component {
  /** @type {QuotedVariant[] | null} */
  #variants = null;

  /** @type {number[] | null} */
  #pinned = null;

  /** @type {string | null} */
  #quotedFor = null;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantSelected, this.#repaint);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#section?.removeEventListener(ThemeEvents.variantSelected, this.#repaint);
  }

  /**
   * The parsed price table, read once.
   *
   * @returns {QuotedVariant[] | null}
   */
  get #table() {
    if (this.#variants) return this.#variants;

    const json = this.querySelector('script[type="application/json"]')?.textContent;
    if (!json) return null;

    try {
      this.#variants = JSON.parse(json);
    } catch {
      return null;
    }

    return this.#variants;
  }

  /**
   * The axes that pin the quote, as positions in `variant.values`.
   *
   * @returns {number[] | null}
   */
  get #pinnedAxes() {
    if (this.#pinned) return this.#pinned;

    const raw = this.dataset.pinnedAxes ?? '';
    const parsed = raw
      .split(',')
      .filter((part) => part !== '')
      .map(Number);
    if (!parsed.length || parsed.some((index) => !Number.isInteger(index) || index < 0))
      return null;

    this.#pinned = parsed;

    return this.#pinned;
  }

  /**
   * @param {Event} event - The variant selection, dispatched the moment the input flips.
   */
  #repaint = (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLElement)) return;

    // A combined listing swaps in another product's picker, and this table only quotes ours.
    if (picker.dataset.productId !== this.dataset.productId) return;

    // That swap morphs `main` and this element has no `id`, so the instance survives with the other
    // product's table. Reset both caches so they do not answer for the previous product.
    if (this.#quotedFor !== this.dataset.productId) {
      this.#variants = null;
      this.#pinned = null;
      this.#quotedFor = this.dataset.productId ?? null;
    }

    const variant = this.#quote(picker);
    const section = this.#section;
    if (!variant || !section) return;

    const format = this.dataset.moneyFormat ?? '';
    const currency = this.dataset.currency ?? '';
    const fromLabel = this.dataset.fromLabel ?? '';
    const productId = this.dataset.productId ?? '';

    for (const container of section.querySelectorAll('[ref="priceContainer"]')) {
      if (!quotesThisProduct(container, productId)) continue;
      paint(container, variant, format, currency, fromLabel);
    }

    for (const badge of section.querySelectorAll('[ref="ctaPillPrice"]')) {
      // No product on the badge means no table may repaint it, which is how the pill stands down
      // on a page whose price block it cannot mirror.
      if (!(badge instanceof HTMLElement) || badge.dataset.productId !== productId) continue;
      paintBadge(badge, variant, format, currency, fromLabel);
    }
  };

  /**
   * Resolves the picker's current selection against the table.
   *
   * Matches only the pinned axes and returns the cheapest row left, which is what the server
   * quotes. Ties keep the first row, so the compare-at travels with the same variant Liquid
   * would have settled on.
   *
   * @param {HTMLElement} picker - The variant picker that dispatched.
   * @returns {QuotedVariant | null}
   */
  #quote(picker) {
    const table = this.#table;
    const pinned = this.#pinnedAxes;
    if (!table || !pinned) return null;

    const selection = readSelection(picker);

    const narrowing = pinned.filter((index) => selection.has(index));
    if (!narrowing.length) return null;

    // An axis the DOM answered for nothing on is only safe to drop where the TABLE holds one value
    // for it, the row a picker prints as text. Dropping one with a choice quotes below the server.
    const dropped = pinned.filter((index) => !selection.has(index));
    if (dropped.some((index) => varies(table, index))) return null;

    /** @type {QuotedVariant | null} */
    let floor = null;
    for (const variant of table) {
      if (!narrowing.every((index) => variant.values[index] === selection.get(index))) continue;
      if (!floor || variant.price < floor.price) floor = variant;
    }

    return floor;
  }
}

/**
 * Whether the table leaves one axis free to drop, which only one recorded value does.
 *
 * An axis the table has no column for answers true as well: two undefineds compare equal, so
 * asking the rows directly would read a pinned axis nothing quotes as settled and drop it.
 *
 * @param {QuotedVariant[]} table - The quoted rows.
 * @param {number} axis - The axis's option position.
 * @returns {boolean}
 */
function varies(table, axis) {
  const [first] = table;
  if (!first || axis >= first.values.length) return true;

  return table.some((variant) => variant.values[axis] !== first.values[axis]);
}

/**
 * The picker's current selection, keyed by each axis's own option position.
 *
 * Keyed rather than listed because the picker renders a control only for an axis that has a choice
 * to make, so a list would silently shift every axis after a single-value one and quote the wrong
 * row. `data-input-id` is `<1-based option position>-<value index>`, on radios and options alike.
 *
 * @param {HTMLElement} picker - The variant picker that dispatched.
 * @returns {Map<number, string>}
 */
function readSelection(picker) {
  /** @type {Map<number, string>} */
  const selection = new Map();

  const controls = picker.querySelectorAll(
    'fieldset input:checked, select.variant-option__select option:checked'
  );
  for (const control of controls) {
    if (!(control instanceof HTMLInputElement) && !(control instanceof HTMLOptionElement)) continue;

    const axis = Number(control.dataset.inputId?.split('-')[0]) - 1;
    if (!Number.isInteger(axis) || axis < 0) continue;

    selection.set(axis, control.value);
  }

  return selection;
}

/**
 * Rewrites the CTA pill's price badge in place.
 *
 * Its own function rather than a branch of `paint`: the badge is a single span with no sale
 * markup, so there is nothing to strike through and nothing to toggle. It quotes what the shopper
 * pays, which is the row's price whether or not that row is on sale.
 *
 * @param {HTMLElement} badge - A `[ref="ctaPillPrice"]` span for this product.
 * @param {QuotedVariant} variant - The variant now selected.
 * @param {string} format - The shop money format the server would have used.
 * @param {string} currency - The active currency code.
 * @param {string} fromLabel - The "From" prefix custom.cta-pill.liquid renders, or ''.
 */
function paintBadge(badge, variant, format, currency, fromLabel) {
  // Only the badges the server actually applied the rule to, the same opt-in `paint` reads: a
  // product with no hidden axis quotes its variant outright and must not gain a "From".
  const label = badge.hasAttribute('data-from-price') ? fromLabel : '';

  badge.textContent = `${label}${formatMoney(variant.price, format, currency)}`;
}

/**
 * Whether a price container is quoting the product this table covers.
 *
 * The section also carries product cards, and a card's price belongs to a different product.
 *
 * @param {Element} container - A `[ref="priceContainer"]` element.
 * @param {string} productId - The product this table quotes.
 * @returns {boolean}
 */
function quotesThisProduct(container, productId) {
  const block = container.closest('product-price');
  if (block instanceof HTMLElement) return block.dataset.productId === productId;

  return Boolean(container.closest('.sticky-add-to-cart__price'));
}

/**
 * Rewrites one price container in place.
 *
 * @param {Element} container - A `[ref="priceContainer"]` element.
 * @param {QuotedVariant} variant - The variant now selected.
 * @param {string} format - The shop money format the server would have used.
 * @param {string} currency - The active currency code.
 * @param {string} fromLabel - The "From" prefix price.liquid renders inside the same span, or ''.
 */
function paint(container, variant, format, currency, fromLabel) {
  const regular = container.querySelector('.price__regular');
  const sale = container.querySelector('.price__sale');

  // Only the containers the server actually applied the rule to. A section can hold a card quoting
  // the same product without it, and price.liquid also drops the rule for volume pricing.
  const label = container.hasAttribute('data-from-price') ? fromLabel : '';

  // Volume pricing prints a range and a unit price restates it per measure; neither is a single
  // number this can retype, so those containers keep waiting for the server render.
  if (!regular || !sale || container.querySelector('.unit-price')) return;

  const onSale =
    typeof variant.compareAtPrice === 'number' && variant.compareAtPrice > variant.price;
  const price = formatMoney(variant.price, format, currency);
  const compareAtPrice =
    typeof variant.compareAtPrice === 'number'
      ? formatMoney(variant.compareAtPrice, format, currency)
      : '';

  for (const element of container.querySelectorAll('.price'))
    element.textContent = `${label}${price}`;
  for (const element of container.querySelectorAll('.compare-at-price'))
    element.textContent = compareAtPrice;

  regular.classList.toggle('price__hidden', onSale);
  sale.classList.toggle('price__hidden', !onSale);
}

if (!customElements.get('custom-price-preview')) {
  customElements.define('custom-price-preview', PricePreview);
}
